/**
 * /settings/backups — backups + staging clones admin page.
 *
 * Two tabs:
 *   Backups   — list, create on-demand, restore (verify-only), prune
 *   Staging   — list active clones, create from latest backup, drop, refresh
 *
 * Admin-only.  Mutations are non-destructive by default (restore lands in a
 * new DB, drop hits an explicit confirm).
 */
import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";

import {
  backupsListQueryOptions,
  maskStrategyQueryOptions,
  stagingListQueryOptions,
  useCreateBackup,
  useCreateClone,
  useDropClone,
  usePruneBackups,
  useRefreshClone,
  useRestoreBackup,
  type Backup,
  type StagingClone,
} from "@/lib/backups";
import { useToast } from "@/components/Toaster";

export const Route = createFileRoute("/_auth/settings/backups")({
  component: BackupsAndStagingPage,
});

type Tab = "backups" | "staging";

// ── helpers ──────────────────────────────────────────────────────────────────

function fmtBytes(n: number | null | undefined): string {
  if (n == null) return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1024 ** 2) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 ** 3) return `${(n / 1024 ** 2).toFixed(1)} MB`;
  return `${(n / 1024 ** 3).toFixed(2)} GB`;
}

function fmtDate(s: string | null | undefined, withTime = true): string {
  if (!s) return "—";
  const d = new Date(s);
  return withTime
    ? d.toLocaleString(undefined, { dateStyle: "short", timeStyle: "short" })
    : d.toLocaleDateString();
}

function fmtRelative(s: string | null | undefined): string {
  if (!s) return "—";
  const target = new Date(s).getTime();
  const now = Date.now();
  const diffMs = target - now;
  const past = diffMs < 0;
  const abs = Math.abs(diffMs);
  const days = Math.floor(abs / (24 * 60 * 60 * 1000));
  const hours = Math.floor((abs / (60 * 60 * 1000)) % 24);
  const label = days > 0 ? `${days}d ${hours}h` : `${hours}h`;
  return past ? `${label} ago` : `in ${label}`;
}

function statusPill(status: string): string {
  const map: Record<string, string> = {
    complete: "bg-green-100 text-green-800",
    active: "bg-green-100 text-green-800",
    running: "bg-blue-100 text-blue-800",
    creating: "bg-blue-100 text-blue-800",
    pending: "bg-amber-100 text-amber-800",
    expired: "bg-amber-100 text-amber-800",
    failed: "bg-red-100 text-red-800",
    pruned: "bg-slate-100 text-slate-600",
    dropped: "bg-slate-100 text-slate-600",
  };
  return map[status] ?? "bg-slate-100 text-slate-700";
}

// ── Main page ────────────────────────────────────────────────────────────────

function BackupsAndStagingPage() {
  const [tab, setTab] = useState<Tab>("backups");

  return (
    <div className="flex flex-col h-[calc(100vh-56px)] overflow-hidden">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 px-6 py-3 dark:bg-gray-900 dark:border-gray-800">
        <div className="flex items-baseline gap-3">
          <h1 className="text-xl font-semibold text-slate-900 dark:text-slate-100">
            Backups &amp; Staging
          </h1>
          <span className="text-xs text-slate-500 dark:text-slate-400">
            Disaster recovery, restore CLI, on-demand staging clones with PII masking
          </span>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white border-b border-slate-200 px-6 dark:bg-gray-900 dark:border-gray-800">
        <div className="flex gap-0">
          {(["backups", "staging"] as Tab[]).map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={
                "px-4 py-2.5 text-sm border-b-2 transition-colors capitalize " +
                (tab === t
                  ? "border-indigo-600 text-indigo-700 font-medium dark:text-indigo-400"
                  : "border-transparent text-slate-600 hover:text-slate-900 dark:text-slate-400 dark:hover:text-slate-200")
              }
            >
              {t}
            </button>
          ))}
        </div>
      </div>

      {/* Body */}
      <div className="flex-1 min-h-0 overflow-auto px-6 py-6">
        {tab === "backups" ? <BackupsTab /> : <StagingTab />}
      </div>
    </div>
  );
}

// ── Backups tab ──────────────────────────────────────────────────────────────

function BackupsTab() {
  const toast = useToast();
  const backupsQ = useQuery(backupsListQueryOptions(100));
  const createMut = useCreateBackup();
  const pruneMut = usePruneBackups();
  const restoreMut = useRestoreBackup();
  const [showCreate, setShowCreate] = useState(false);
  const [createForm, setCreateForm] = useState({
    retention_days: "30",
    notes: "",
    run_in_background: true,
  });

  const backups = backupsQ.data ?? [];

  const totalSize = useMemo(
    () => backups.filter(b => b.status === "complete").reduce((s, b) => s + (b.size_bytes ?? 0), 0),
    [backups],
  );

  async function handleCreate(e: React.FormEvent) {
    e.preventDefault();
    try {
      const result = await createMut.mutateAsync({
        kind: "full",
        retention_days: parseInt(createForm.retention_days, 10) || 30,
        notes: createForm.notes || null,
        run_in_background: createForm.run_in_background,
      });
      toast.success(
        createForm.run_in_background
          ? `Backup ${result.id.slice(0, 8)} queued — refresh to see progress.`
          : `Backup ${result.id.slice(0, 8)} complete (${fmtBytes(result.size_bytes)}).`,
      );
      setShowCreate(false);
      setCreateForm({ retention_days: "30", notes: "", run_in_background: true });
      backupsQ.refetch();
    } catch (err) {
      toast.error(`Backup failed: ${(err as Error).message}`);
    }
  }

  async function handlePrune() {
    if (!window.confirm("Delete backups past their retention window?  This deletes the archive files but keeps the metadata rows.")) return;
    try {
      const r = await pruneMut.mutateAsync();
      toast.success(`Pruned ${r.pruned} of ${r.considered} candidates (${r.errors} errors).`);
    } catch (err) {
      toast.error(`Prune failed: ${(err as Error).message}`);
    }
  }

  async function handleVerify(b: Backup) {
    try {
      const r = await restoreMut.mutateAsync({ backup_id: b.id, verify_only: true });
      toast.success(`Archive verified — ${(r["verified_pg_files"] as string[] | undefined)?.length ?? 0} pg files, ${(r["verified_mongo_files"] as string[] | undefined)?.length ?? 0} mongo archives.`);
    } catch (err) {
      toast.error(`Verify failed: ${(err as Error).message}`);
    }
  }

  async function handleRestore(b: Backup) {
    const target = window.prompt(
      `Restore backup ${b.id.slice(0, 8)}?\n\n` +
      "Enter target Postgres DB name (defaults to ledger_restore_<short-id>).\n" +
      "Leave blank to use the safe default — this NEVER clobbers production unless you type the prod DB name.",
      "",
    );
    if (target === null) return;
    try {
      const r = await restoreMut.mutateAsync({
        backup_id: b.id,
        target_pg_db: target || null,
      });
      toast.success(`Restored to ${r["target_pg_db"]}.  Connect with psql to inspect.`);
    } catch (err) {
      toast.error(`Restore failed: ${(err as Error).message}`);
    }
  }

  return (
    <div className="space-y-4">
      {/* Summary + actions */}
      <div className="bg-white dark:bg-gray-900 border border-slate-200 dark:border-gray-800 rounded-lg p-4 flex items-center gap-6 flex-wrap">
        <div>
          <div className="text-xs text-slate-500 uppercase tracking-wide">Total backups</div>
          <div className="text-2xl font-semibold text-slate-900 dark:text-slate-100">
            {backups.filter(b => b.status === "complete").length}
            <span className="text-sm font-normal text-slate-500 ml-2">
              ({fmtBytes(totalSize)})
            </span>
          </div>
        </div>
        <div>
          <div className="text-xs text-slate-500 uppercase tracking-wide">Latest</div>
          <div className="text-sm font-mono text-slate-700 dark:text-slate-300">
            {backups[0] ? fmtRelative(backups[0].created_at) : "—"}
          </div>
        </div>
        <div className="flex-1" />
        <button
          onClick={() => setShowCreate((s) => !s)}
          className="text-sm bg-indigo-600 hover:bg-indigo-700 text-white rounded-md px-4 py-1.5"
        >
          + Create backup now
        </button>
        <button
          onClick={handlePrune}
          disabled={pruneMut.isPending}
          className="text-sm border border-slate-300 hover:bg-slate-50 rounded-md px-3 py-1.5 disabled:opacity-50 dark:border-gray-700"
        >
          {pruneMut.isPending ? "Pruning…" : "Run pruner"}
        </button>
      </div>

      {/* Create form */}
      {showCreate && (
        <form onSubmit={handleCreate} className="bg-indigo-50 dark:bg-indigo-950/30 border border-indigo-200 dark:border-indigo-800 rounded-lg p-4 space-y-3">
          <h3 className="text-sm font-semibold text-indigo-900 dark:text-indigo-200">Create a backup now</h3>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-slate-700 dark:text-slate-300 mb-1">
                Retention (days)
              </label>
              <input
                type="number"
                min="1"
                max="3650"
                value={createForm.retention_days}
                onChange={(e) => setCreateForm(f => ({ ...f, retention_days: e.target.value }))}
                className="w-full border border-slate-300 dark:border-gray-700 dark:bg-gray-900 rounded-md px-2 py-1 text-sm"
              />
            </div>
            <div>
              <label className="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-300 mt-5 cursor-pointer">
                <input
                  type="checkbox"
                  checked={createForm.run_in_background}
                  onChange={(e) => setCreateForm(f => ({ ...f, run_in_background: e.target.checked }))}
                />
                Run in background (recommended for prod-sized DBs)
              </label>
            </div>
          </div>
          <div>
            <label className="block text-xs text-slate-700 dark:text-slate-300 mb-1">
              Notes (optional)
            </label>
            <input
              type="text"
              value={createForm.notes}
              onChange={(e) => setCreateForm(f => ({ ...f, notes: e.target.value }))}
              placeholder="e.g. pre-deploy snapshot"
              className="w-full border border-slate-300 dark:border-gray-700 dark:bg-gray-900 rounded-md px-2 py-1 text-sm"
            />
          </div>
          <div className="flex gap-2">
            <button
              type="submit"
              disabled={createMut.isPending}
              className="bg-indigo-600 hover:bg-indigo-700 text-white text-sm rounded-md px-4 py-1.5 disabled:opacity-50"
            >
              {createMut.isPending ? "Creating…" : "Create"}
            </button>
            <button
              type="button"
              onClick={() => setShowCreate(false)}
              className="text-sm text-slate-500 px-3 py-1.5 hover:text-slate-700"
            >
              Cancel
            </button>
          </div>
        </form>
      )}

      {/* Backup list */}
      <div className="bg-white dark:bg-gray-900 border border-slate-200 dark:border-gray-800 rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 dark:bg-gray-800 border-b border-slate-200 dark:border-gray-700">
            <tr>
              <th className="text-left px-4 py-2 text-xs uppercase tracking-wide text-slate-500">When</th>
              <th className="text-left px-4 py-2 text-xs uppercase tracking-wide text-slate-500">Kind</th>
              <th className="text-left px-4 py-2 text-xs uppercase tracking-wide text-slate-500">Trigger</th>
              <th className="text-left px-4 py-2 text-xs uppercase tracking-wide text-slate-500">Status</th>
              <th className="text-right px-4 py-2 text-xs uppercase tracking-wide text-slate-500">Size</th>
              <th className="text-left px-4 py-2 text-xs uppercase tracking-wide text-slate-500">Retention</th>
              <th className="text-left px-4 py-2 text-xs uppercase tracking-wide text-slate-500">Notes</th>
              <th className="px-4 py-2" />
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 dark:divide-gray-800">
            {backups.length === 0 && !backupsQ.isLoading && (
              <tr><td colSpan={8} className="text-center text-sm text-slate-400 py-8">No backups yet — click <strong>+ Create backup now</strong>.</td></tr>
            )}
            {backupsQ.isLoading && (
              <tr><td colSpan={8} className="text-center text-sm text-slate-400 py-8">Loading…</td></tr>
            )}
            {backups.map((b) => (
              <tr key={b.id} className="hover:bg-slate-50 dark:hover:bg-gray-800">
                <td className="px-4 py-2.5 font-mono text-xs text-slate-600 dark:text-slate-400">
                  {fmtDate(b.created_at)}
                  <div className="text-[10px] text-slate-400">{b.id.slice(0, 8)}</div>
                </td>
                <td className="px-4 py-2.5 text-slate-700 dark:text-slate-300">{b.kind}</td>
                <td className="px-4 py-2.5 text-slate-600 dark:text-slate-400">{b.trigger}</td>
                <td className="px-4 py-2.5">
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${statusPill(b.status)}`}>
                    {b.status}
                  </span>
                  {b.encrypted && (
                    <span className="ml-1 text-xs text-indigo-600" title="Fernet-encrypted">🔒</span>
                  )}
                </td>
                <td className="px-4 py-2.5 text-right font-mono text-slate-700 dark:text-slate-300">
                  {fmtBytes(b.size_bytes)}
                </td>
                <td className="px-4 py-2.5 text-xs text-slate-500" title={b.retained_until ?? ""}>
                  {b.retained_until ? fmtRelative(b.retained_until) : "—"}
                </td>
                <td className="px-4 py-2.5 text-xs text-slate-500 max-w-xs truncate" title={b.notes ?? ""}>
                  {b.notes || <span className="text-slate-300">—</span>}
                </td>
                <td className="px-4 py-2.5 text-right whitespace-nowrap">
                  {b.status === "complete" && (
                    <>
                      <button
                        onClick={() => handleVerify(b)}
                        className="text-xs text-slate-600 hover:text-slate-900 dark:text-slate-400 dark:hover:text-slate-200 mr-2"
                        title="Verify archive integrity (no DB writes)"
                      >
                        Verify
                      </button>
                      <button
                        onClick={() => handleRestore(b)}
                        className="text-xs text-indigo-600 hover:text-indigo-800 dark:text-indigo-400"
                        title="Restore to a new DB (never overwrites prod by default)"
                      >
                        Restore
                      </button>
                    </>
                  )}
                  {b.status === "failed" && b.error_message && (
                    <span className="text-xs text-red-600" title={b.error_message}>
                      ⚠ failed
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Help */}
      <div className="text-xs text-slate-500 dark:text-slate-400 leading-relaxed">
        Backups capture both Postgres (<code>ledger</code>) and MongoDB (<code>ledger_money</code>) into a single tar.gz.
        Storage backend is configured via <code>BACKUP_STORAGE_KIND</code> (default: <code>local</code> →{" "}
        <code className="font-mono">/var/lib/ledger-core/backups</code>).
        Restores default to a new DB named <code>ledger_restore_&lt;short-id&gt;</code> — they never
        clobber production unless the target name exactly matches and <code>overwrite=true</code> is passed via the API.
        See <code>docs/help/backups-staging/restore-playbook.md</code> for full recovery scenarios.
      </div>
    </div>
  );
}

// ── Staging tab ──────────────────────────────────────────────────────────────

function StagingTab() {
  const toast = useToast();
  const clonesQ = useQuery(stagingListQueryOptions(false));
  const backupsQ = useQuery(backupsListQueryOptions(50));
  const maskQ = useQuery(maskStrategyQueryOptions());
  const createMut = useCreateClone();
  const dropMut = useDropClone();
  const refreshMut = useRefreshClone();
  const [showCreate, setShowCreate] = useState(false);
  const [showMaskInfo, setShowMaskInfo] = useState(false);
  const [createForm, setCreateForm] = useState({
    name: "",
    source_backup_id: "",
    expires_in_days: "7",
    apply_pii_mask: true,
    notes: "",
  });

  const clones = clonesQ.data ?? [];
  const completedBackups = (backupsQ.data ?? []).filter(b => b.status === "complete");

  async function handleCreate(e: React.FormEvent) {
    e.preventDefault();
    if (!createForm.name.match(/^[a-z0-9][a-z0-9-]{1,40}[a-z0-9]$/)) {
      toast.error("Name must be 3-42 chars, lowercase + hyphens, no leading/trailing hyphen.");
      return;
    }
    try {
      const clone = await createMut.mutateAsync({
        name: createForm.name,
        source_backup_id: createForm.source_backup_id || null,
        expires_in_days: parseInt(createForm.expires_in_days, 10) || 7,
        apply_pii_mask: createForm.apply_pii_mask,
        notes: createForm.notes || null,
      });
      toast.success(`Clone "${clone.name}" created — target DB: ${clone.target_pg_db}`);
      setShowCreate(false);
      setCreateForm({ name: "", source_backup_id: "", expires_in_days: "7", apply_pii_mask: true, notes: "" });
    } catch (err) {
      toast.error(`Clone failed: ${(err as Error).message}`);
    }
  }

  async function handleDrop(c: StagingClone) {
    if (!window.confirm(`Drop staging clone "${c.name}"?\n\nThis deletes both databases:\n  ${c.target_pg_db}\n  ${c.target_mongo_db}\n\nThis cannot be undone.`)) return;
    try {
      await dropMut.mutateAsync(c.id);
      toast.success(`Dropped "${c.name}".`);
    } catch (err) {
      toast.error(`Drop failed: ${(err as Error).message}`);
    }
  }

  async function handleRefresh(c: StagingClone) {
    const days = parseInt(window.prompt("Extend by how many days?", "7") ?? "0", 10);
    if (!days || days < 1) return;
    try {
      await refreshMut.mutateAsync({ cloneId: c.id, extendDays: days });
      toast.success(`Extended "${c.name}" by ${days} day${days === 1 ? "" : "s"}.`);
    } catch (err) {
      toast.error(`Refresh failed: ${(err as Error).message}`);
    }
  }

  return (
    <div className="space-y-4">
      {/* Summary + actions */}
      <div className="bg-white dark:bg-gray-900 border border-slate-200 dark:border-gray-800 rounded-lg p-4 flex items-center gap-6 flex-wrap">
        <div>
          <div className="text-xs text-slate-500 uppercase tracking-wide">Active clones</div>
          <div className="text-2xl font-semibold text-slate-900 dark:text-slate-100">
            {clones.filter(c => c.status === "active").length}
          </div>
        </div>
        <div>
          <div className="text-xs text-slate-500 uppercase tracking-wide">Available backups</div>
          <div className="text-sm font-mono text-slate-700 dark:text-slate-300">
            {completedBackups.length}
          </div>
        </div>
        <div className="flex-1" />
        <button
          onClick={() => setShowMaskInfo((s) => !s)}
          className="text-sm border border-slate-300 hover:bg-slate-50 rounded-md px-3 py-1.5 dark:border-gray-700"
        >
          🔍 What's masked?
        </button>
        <button
          onClick={() => setShowCreate((s) => !s)}
          disabled={completedBackups.length === 0}
          className="text-sm bg-indigo-600 hover:bg-indigo-700 text-white rounded-md px-4 py-1.5 disabled:opacity-50"
          title={completedBackups.length === 0 ? "Create a backup first" : ""}
        >
          + Create clone from backup
        </button>
      </div>

      {/* Mask strategy popover */}
      {showMaskInfo && maskQ.data && (
        <div className="bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-lg p-4">
          <h3 className="text-sm font-semibold text-amber-900 dark:text-amber-200 mb-2">
            PII columns sanitized in every clone ({maskQ.data.length} total)
          </h3>
          <div className="grid grid-cols-2 gap-x-6 text-xs font-mono text-slate-700 dark:text-slate-300">
            {maskQ.data.map((m) => (
              <div key={`${m.table}.${m.column}`} className="flex justify-between py-0.5">
                <span>{m.table}.{m.column}</span>
                <span className="text-amber-700 dark:text-amber-400">→ {m.func}</span>
              </div>
            ))}
          </div>
          <p className="text-xs text-amber-700 dark:text-amber-400 mt-3 leading-relaxed">
            Masking is <strong>deterministic per clone</strong> (the same input → same output within
            this clone) and <strong>unreversible</strong> (HMAC-SHA256, not encryption).  Bank routing →{" "}
            <code>121000248</code> (Wells Fargo SF, publicly known + safe for testing).  Emails →{" "}
            <code>user-XXXXXXXX@staging.example.com</code>.
          </p>
        </div>
      )}

      {/* Create form */}
      {showCreate && (
        <form onSubmit={handleCreate} className="bg-indigo-50 dark:bg-indigo-950/30 border border-indigo-200 dark:border-indigo-800 rounded-lg p-4 space-y-3">
          <h3 className="text-sm font-semibold text-indigo-900 dark:text-indigo-200">
            Create staging clone
          </h3>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-slate-700 dark:text-slate-300 mb-1">
                Name <span className="text-slate-400">(3-42 chars, a-z 0-9 -)</span>
              </label>
              <input
                required
                type="text"
                pattern="[a-z0-9][a-z0-9-]{1,40}[a-z0-9]"
                value={createForm.name}
                onChange={(e) => setCreateForm(f => ({ ...f, name: e.target.value }))}
                placeholder="q2-budget-test"
                className="w-full border border-slate-300 dark:border-gray-700 dark:bg-gray-900 rounded-md px-2 py-1 text-sm font-mono"
              />
            </div>
            <div>
              <label className="block text-xs text-slate-700 dark:text-slate-300 mb-1">
                Expires in (days)
              </label>
              <input
                type="number"
                min="1"
                max="90"
                value={createForm.expires_in_days}
                onChange={(e) => setCreateForm(f => ({ ...f, expires_in_days: e.target.value }))}
                className="w-full border border-slate-300 dark:border-gray-700 dark:bg-gray-900 rounded-md px-2 py-1 text-sm"
              />
            </div>
          </div>
          <div>
            <label className="block text-xs text-slate-700 dark:text-slate-300 mb-1">
              Source backup
            </label>
            <select
              value={createForm.source_backup_id}
              onChange={(e) => setCreateForm(f => ({ ...f, source_backup_id: e.target.value }))}
              className="w-full border border-slate-300 dark:border-gray-700 dark:bg-gray-900 rounded-md px-2 py-1 text-sm"
            >
              <option value="">— latest completed backup —</option>
              {completedBackups.map((b) => (
                <option key={b.id} value={b.id}>
                  {fmtDate(b.created_at)} · {b.id.slice(0, 8)} · {fmtBytes(b.size_bytes)}
                  {b.notes ? ` — ${b.notes}` : ""}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs text-slate-700 dark:text-slate-300 mb-1">
              Notes (optional)
            </label>
            <input
              type="text"
              value={createForm.notes}
              onChange={(e) => setCreateForm(f => ({ ...f, notes: e.target.value }))}
              placeholder="e.g. test the new GMP migration"
              className="w-full border border-slate-300 dark:border-gray-700 dark:bg-gray-900 rounded-md px-2 py-1 text-sm"
            />
          </div>
          <label className="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-300 cursor-pointer">
            <input
              type="checkbox"
              checked={createForm.apply_pii_mask}
              onChange={(e) => setCreateForm(f => ({ ...f, apply_pii_mask: e.target.checked }))}
            />
            Apply PII masking <span className="text-xs text-slate-500">(recommended — sanitizes SSN/bank/email/phone)</span>
          </label>
          <div className="flex gap-2">
            <button
              type="submit"
              disabled={createMut.isPending}
              className="bg-indigo-600 hover:bg-indigo-700 text-white text-sm rounded-md px-4 py-1.5 disabled:opacity-50"
            >
              {createMut.isPending ? "Creating clone…" : "Create"}
            </button>
            <button
              type="button"
              onClick={() => setShowCreate(false)}
              className="text-sm text-slate-500 px-3 py-1.5 hover:text-slate-700"
            >
              Cancel
            </button>
          </div>
        </form>
      )}

      {/* Clone list */}
      <div className="bg-white dark:bg-gray-900 border border-slate-200 dark:border-gray-800 rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 dark:bg-gray-800 border-b border-slate-200 dark:border-gray-700">
            <tr>
              <th className="text-left px-4 py-2 text-xs uppercase tracking-wide text-slate-500">Name</th>
              <th className="text-left px-4 py-2 text-xs uppercase tracking-wide text-slate-500">Status</th>
              <th className="text-left px-4 py-2 text-xs uppercase tracking-wide text-slate-500">PII</th>
              <th className="text-left px-4 py-2 text-xs uppercase tracking-wide text-slate-500">Target DB</th>
              <th className="text-left px-4 py-2 text-xs uppercase tracking-wide text-slate-500">Created</th>
              <th className="text-left px-4 py-2 text-xs uppercase tracking-wide text-slate-500">Expires</th>
              <th className="text-left px-4 py-2 text-xs uppercase tracking-wide text-slate-500">Notes</th>
              <th className="px-4 py-2" />
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 dark:divide-gray-800">
            {clones.length === 0 && !clonesQ.isLoading && (
              <tr><td colSpan={8} className="text-center text-sm text-slate-400 py-8">
                No active clones.
                {completedBackups.length === 0
                  ? <span className="block mt-1">Create a backup first, then come back here.</span>
                  : <span className="block mt-1">Click <strong>+ Create clone from backup</strong> above.</span>}
              </td></tr>
            )}
            {clonesQ.isLoading && (
              <tr><td colSpan={8} className="text-center text-sm text-slate-400 py-8">Loading…</td></tr>
            )}
            {clones.map((c) => {
              const expired = new Date(c.expires_at) < new Date();
              return (
                <tr key={c.id} className="hover:bg-slate-50 dark:hover:bg-gray-800">
                  <td className="px-4 py-2.5">
                    <div className="font-mono text-sm text-slate-800 dark:text-slate-200">{c.name}</div>
                    <div className="text-[10px] text-slate-400">{c.id.slice(0, 8)}</div>
                  </td>
                  <td className="px-4 py-2.5">
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${statusPill(c.status)}`}>
                      {c.status}
                    </span>
                    {expired && c.status === "active" && (
                      <span className="ml-1 text-xs text-amber-600" title="Past expires_at — next sweep will drop it">⏰</span>
                    )}
                  </td>
                  <td className="px-4 py-2.5">
                    {c.pii_masked ? (
                      <span className="text-xs text-green-700 dark:text-green-400">✓ masked</span>
                    ) : (
                      <span className="text-xs text-red-600">⚠ not masked</span>
                    )}
                  </td>
                  <td className="px-4 py-2.5 font-mono text-xs text-slate-600 dark:text-slate-400">
                    {c.target_pg_db}
                  </td>
                  <td className="px-4 py-2.5 text-xs text-slate-500">{fmtDate(c.created_at, false)}</td>
                  <td className="px-4 py-2.5 text-xs text-slate-500" title={c.expires_at}>
                    {fmtRelative(c.expires_at)}
                  </td>
                  <td className="px-4 py-2.5 text-xs text-slate-500 max-w-xs truncate" title={c.notes ?? ""}>
                    {c.notes || <span className="text-slate-300">—</span>}
                  </td>
                  <td className="px-4 py-2.5 text-right whitespace-nowrap">
                    {c.status === "active" && (
                      <>
                        <button
                          onClick={() => handleRefresh(c)}
                          className="text-xs text-slate-600 hover:text-slate-900 dark:text-slate-400 mr-2"
                          title="Bump expires_at"
                        >
                          Refresh
                        </button>
                        <button
                          onClick={() => handleDrop(c)}
                          className="text-xs text-red-600 hover:text-red-800"
                        >
                          Drop
                        </button>
                      </>
                    )}
                    {c.status === "failed" && c.error_message && (
                      <span className="text-xs text-red-600" title={c.error_message}>⚠ failed</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Help */}
      <div className="text-xs text-slate-500 dark:text-slate-400 leading-relaxed">
        Staging clones spin up a separate Postgres + Mongo database populated from a backup, with PII automatically sanitized.
        Use them to test imports, practice recovery, or train new staff on real-shape data.
        Connect to the clone DB directly from your tools:{" "}
        <code className="font-mono">host: db, dbname: ledger_staging_&lt;name&gt;</code>.
        Clones auto-drop after their expiry — click Refresh to extend.
      </div>
    </div>
  );
}
